import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/Select';
import { useDepartmentStore } from '../../../store/departmentStore';

const ADD_NEW = '__add_new__';

interface DepartmentFieldProps {
  /** Department name — people.employees.department stores the name, not the id. */
  value: string;
  onChange: (name: string) => void;
  invalid?: boolean;
}

/**
 * Department picker backed by people.departments.
 *
 * "Add new department" creates the row in people.departments first, so every
 * name saved on an employee exists in the master table.
 */
export function DepartmentField({ value, onChange, invalid }: DepartmentFieldProps) {
  const { departments, isLoaded, isLoading, error, fetchDepartments, createDepartment } = useDepartmentStore();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // An employee saved before this list existed may hold a name that is not in
  // the table; keep it selectable so opening the edit form does not wipe it.
  const isLegacy = !!value && !departments.some((d) => d.name.toLowerCase() === value.toLowerCase());

  const handleSelect = (val: string) => {
    if (val === ADD_NEW) {
      setAdding(true);
      return;
    }
    onChange(val);
  };

  const save = async () => {
    const name = newName.trim();
    if (!name) return;
    const existing = departments.find((d) => d.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      onChange(existing.name);
      setAdding(false);
      setNewName('');
      return;
    }
    setSaving(true);
    try {
      const created = await createDepartment(name);
      onChange(created.name);
      toast.success(`Department "${created.name}" added`);
      setAdding(false);
      setNewName('');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not add the department');
    } finally {
      setSaving(false);
    }
  };

  if (adding) {
    return (
      <div className="flex gap-2">
        <Input
          id="department"
          placeholder="e.g. Infrastructure & DevOps"
          autoFocus
          value={newName}
          maxLength={100}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); save(); }
            if (e.key === 'Escape') { setAdding(false); setNewName(''); }
          }}
        />
        <Button type="button" size="sm" variant="secondary" onClick={save} disabled={saving || !newName.trim()}>
          {saving ? <Loader2 className="animate-spin" /> : 'Add'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => { setAdding(false); setNewName(''); }}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Select value={value || ''} onValueChange={handleSelect} disabled={isLoading && !isLoaded}>
        <SelectTrigger id="department" invalid={invalid}>
          <SelectValue placeholder={isLoading && !isLoaded ? 'Loading departments…' : 'Select department'} />
        </SelectTrigger>
        <SelectContent>
          {departments.map((d) => (
            <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
          ))}
          {isLegacy && (
            <SelectItem value={value}>{value} (not in department list)</SelectItem>
          )}
          <SelectItem value={ADD_NEW}>
            <span className="inline-flex items-center gap-1.5 text-primary"><Plus className="size-3.5" /> Add new department</span>
          </SelectItem>
        </SelectContent>
      </Select>
      {error && <p className="text-xs text-destructive-strong">{error}</p>}
      {isLoaded && !error && departments.length === 0 && (
        <p className="text-xs text-muted-foreground">No departments yet — add the first one.</p>
      )}
    </div>
  );
}
